import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { Bold, Italic, Underline, List, ListOrdered, Link, FileText } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAppData } from '@/contexts/AppDataContext'

interface NoteFormProps {
    formData: any
    onSubmit: (data: any) => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
    /** Si la stat est déjà en base (note après stat), message de confirmation adapté */
    confirmNavigateHomeCopy?: { title: string; message: string }
}

// Use <div> blocks so each section is a separate block context — prevents bold inheritance
const DEFAULT_NOTE_TEMPLATE = `<div><strong>Raison du rendez-vous :</strong></div><div><br></div><div><strong>Contexte et déroulement :</strong></div><div><br></div><div><strong>Actions définies :</strong></div><ul><li></li><li></li></ul>`

export const NoteForm = ({
    formData,
    onSubmit,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm,
    confirmNavigateHomeCopy
}: NoteFormProps) => {
    const NOTE_TYPES = ['entretien', "réunion d’équipe", 'anamnèse', 'contacts réseau']
    const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

    const [noteData] = useState({
        uuid: formData.noteData?.uuid || crypto.randomUUID(),
        locataire: formData.entryData?.locataire || formData.noteData?.locataire || '',
        travailleur: formData.entryData?.travailleur || formData.noteData?.travailleur || '',
        collegue: formData.entryData?.collegue || formData.noteData?.collegue || [],
        date: formData.entryData?.date || formData.noteData?.date || new Date().toISOString().split('T')[0],
        note: formData.noteData?.note || DEFAULT_NOTE_TEMPLATE,
        submit_ts: formData.noteData?.submit_ts || null
    })
    const [noteType, setNoteType] = useState<string>(formData.noteData?.type || '')

    const [errors, setErrors] = useState<string[]>([])
    const [formatState, setFormatState] = useState({ bold: false, italic: false, underline: false })
    const editorRef = useRef<HTMLDivElement>(null)

    const { workers: contextWorkers } = useAppData()
    const workers = contextWorkers.filter(w => w.name).map(w => ({ id: w.id, name: w.name! }))

    // @mention state
    const [workerIdsWithProfile, setWorkerIdsWithProfile] = useState<Set<string>>(new Set())
    const [mentionQuery, setMentionQuery] = useState<string | null>(null)
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null)
    const [mentionedWorkers, setMentionedWorkers] = useState<{ id: string; name: string }[]>([])
    const [activeDropdownIndex, setActiveDropdownIndex] = useState(-1)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Store last detected mention range (critical for mobile — selection may shift after touch)
    const lastMentionRangeRef = useRef<Range | null>(null)

    // Fetch profiles for @mention (only this small query needed now)
    useEffect(() => {
        if (IS_DEMO_MODE) return
        supabase.from('profiles').select('worker_id').not('worker_id', 'is', null).then(({ data }) => {
            if (data) {
                setWorkerIdsWithProfile(new Set(
                    data.map(p => p.worker_id).filter(Boolean) as string[]
                ))
            }
        })
    }, [])

    // The demo has no accounts: every demo worker can be mentioned.
    useEffect(() => {
        if (IS_DEMO_MODE) setWorkerIdsWithProfile(new Set(contextWorkers.map((w) => w.id)))
    }, [contextWorkers])

    // Init editor content + re-extract mentions when coming back from summary
    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.innerHTML = formData.noteData?.note || DEFAULT_NOTE_TEMPLATE
            const spans = editorRef.current.querySelectorAll('.mention[data-worker-id]')
            const existing: { id: string; name: string }[] = []
            spans.forEach(span => {
                const id = span.getAttribute('data-worker-id')
                const name = (span.textContent || '').replace('@', '')
                if (id && !existing.find(w => w.id === id)) existing.push({ id, name })
            })
            if (existing.length > 0) setMentionedWorkers(existing)
        }
    }, [])

    // Reset dropdown highlight when query changes — start at 0 so Enter always picks first
    useEffect(() => {
        setActiveDropdownIndex(0)
    }, [mentionQuery])

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setMentionQuery(null)
                setDropdownPos(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        const editor = editorRef.current
        if (!editor) return
        const handleSelectionChange = () => updateFormatState()
        const handleMouseUp = () => updateFormatState()
        document.addEventListener('selectionchange', handleSelectionChange)
        editor.addEventListener('mouseup', handleMouseUp)
        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange)
            editor.removeEventListener('mouseup', handleMouseUp)
        }
    }, [])

    // DOM-based format state — avoids inheriting bold from adjacent elements
    const updateFormatState = () => {
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount || !editorRef.current) {
            setFormatState({ bold: false, italic: false, underline: false })
            return
        }
        const range = sel.getRangeAt(0)
        let node: Node | null = range.startContainer
        let isBold = false, isItalic = false, isUnderline = false

        while (node && node !== editorRef.current) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement
                const tag = el.tagName
                if (tag === 'STRONG' || tag === 'B') isBold = true
                if (tag === 'EM' || tag === 'I') isItalic = true
                if (tag === 'U') isUnderline = true
            }
            node = node.parentNode
        }
        setFormatState({ bold: isBold, italic: isItalic, underline: isUnderline })
    }

    // Re-scan DOM and sync mentionedWorkers (handles deletions from editor)
    const syncMentionedWorkers = () => {
        if (!editorRef.current) return
        const spans = editorRef.current.querySelectorAll('.mention[data-worker-id]')
        const found: { id: string; name: string }[] = []
        spans.forEach(span => {
            const id = span.getAttribute('data-worker-id')
            const name = (span.textContent || '').replace('@', '')
            if (id && !found.find(w => w.id === id)) found.push({ id, name })
        })
        setMentionedWorkers(found)
    }

    // Compute filtered workers: enabled (with profile) first, disabled at bottom
    const allFilteredWorkers = mentionQuery !== null
        ? workers.filter(w => w.name.toLowerCase().startsWith(mentionQuery.toLowerCase()))
        : []
    const enabledFiltered = allFilteredWorkers.filter(w => workerIdsWithProfile.has(w.id))
    const disabledFiltered = allFilteredWorkers.filter(w => !workerIdsWithProfile.has(w.id))
    const dropdownWorkers = [...enabledFiltered, ...disabledFiltered]

    // Get caret rect — with iOS fallback using a temporary span
    const getCaretRect = (range: Range): DOMRect | null => {
        const r = range.cloneRange()
        r.collapse(true)

        // Try getClientRects first
        const rects = r.getClientRects()
        if (rects.length > 0) return rects[rects.length - 1]

        // Try getBoundingClientRect
        const rect = r.getBoundingClientRect()
        if (rect && (rect.width > 0 || rect.height > 0)) return rect

        // iOS fallback: insert a temp zero-width span, measure it, then remove
        try {
            const tempSpan = document.createElement('span')
            tempSpan.style.display = 'inline-block'
            tempSpan.style.height = '1em'
            tempSpan.innerHTML = '&#8203;'
            r.insertNode(tempSpan)
            const spanRect = tempSpan.getBoundingClientRect()
            tempSpan.parentNode?.removeChild(tempSpan)
            // Normalize whitespace collapsed by the temp span removal
            r.startContainer?.normalize?.()
            if (spanRect.top !== 0 || spanRect.left !== 0) return spanRect
        } catch {
            // ignore
        }

        return null
    }

    const computeDropdownPosition = (caretRect: DOMRect | null) => {
        // Fallback: position below the editor
        if (!caretRect) {
            if (!editorRef.current) return null
            const editorRect = editorRef.current.getBoundingClientRect()
            return { top: editorRect.bottom + 4, left: editorRect.left + 8 }
        }

        const PADDING = 8
        const MIN_WIDTH = 192
        const MAX_HEIGHT = 208

        let left = caretRect.left
        left = Math.max(PADDING, Math.min(left, window.innerWidth - MIN_WIDTH - PADDING))

        const belowTop = caretRect.bottom + 4
        const aboveTop = caretRect.top - 4 - MAX_HEIGHT
        let top = belowTop
        if (belowTop + MAX_HEIGHT + PADDING > window.innerHeight) top = aboveTop
        top = Math.max(PADDING, Math.min(top, window.innerHeight - PADDING))

        return { top, left }
    }

    const placeCaretAfterMention = (mentionEl: HTMLElement) => {
        const sel = window.getSelection()
        if (!sel || !editorRef.current) return

        let spacerNode = mentionEl.nextSibling
        if (!spacerNode || spacerNode.nodeType !== Node.TEXT_NODE) {
            spacerNode = document.createTextNode('\u00A0')
            mentionEl.after(spacerNode)
        }

        const textNode = spacerNode as Text
        const range = document.createRange()
        range.setStart(textNode, Math.min(1, textNode.textContent?.length ?? 0))
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
        editorRef.current.focus()
    }

    // Detect @mention in current selection — deferred for iOS (selection updates async)
    const detectMention = () => {
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount) { setMentionQuery(null); return }

        const range = sel.getRangeAt(0)
        const node = range.startContainer
        if (node.nodeType !== Node.TEXT_NODE) { setMentionQuery(null); return }

        const textBeforeCaret = (node.textContent || '').slice(0, range.startOffset)
        const match = textBeforeCaret.match(/@([^\s]*)$/)

        if (!match) { setMentionQuery(null); return }

        // Store range for mobile (selection may shift before we can use it)
        lastMentionRangeRef.current = range.cloneRange()

        setMentionQuery(match[1])
        const rect = getCaretRect(range)
        setDropdownPos(computeDropdownPosition(rect))
    }

    const handleEditorInput = () => {
        updateFormatState()
        syncMentionedWorkers()
        // Defer selection read — critical on iOS where selection lags behind input events
        setTimeout(detectMention, 0)
    }

    const handleEditorClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement | null
        const mentionEl = target?.closest?.('.mention[data-worker-id]') as HTMLElement | null
        if (!mentionEl) return
        e.preventDefault()
        placeCaretAfterMention(mentionEl)
    }

    const handleEditorKeyDown = (e: React.KeyboardEvent) => {
        // Arrow key navigation and Enter when dropdown is open
        if (mentionQuery !== null && enabledFiltered.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveDropdownIndex(prev => Math.min(prev + 1, enabledFiltered.length - 1))
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveDropdownIndex(prev => Math.max(prev - 1, 0))
                return
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                insertMention(enabledFiltered[Math.max(0, activeDropdownIndex)])
                return
            }
        }

        // Enter outside dropdown: if inside a bold/strong block, create a clean non-bold new line
        if (e.key === 'Enter' && mentionQuery === null) {
            const sel = window.getSelection()
            if (sel && sel.rangeCount) {
                const range = sel.getRangeAt(0)
                let node: Node | null = range.startContainer
                let boldAncestor: HTMLElement | null = null

                while (node && node !== editorRef.current) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const el = node as HTMLElement
                        if (el.tagName === 'STRONG' || el.tagName === 'B') boldAncestor = el
                    }
                    node = node.parentNode
                }

                if (boldAncestor) {
                    e.preventDefault()
                    // Find the block-level element (direct child of editor) containing the bold
                    let blockEl: HTMLElement | null = boldAncestor
                    while (blockEl && blockEl.parentElement !== editorRef.current) {
                        blockEl = blockEl.parentElement
                    }
                    if (blockEl && blockEl !== editorRef.current) {
                        const newDiv = document.createElement('div')
                        newDiv.innerHTML = '<br>'
                        blockEl.after(newDiv)
                        const newRange = document.createRange()
                        newRange.setStart(newDiv, 0)
                        newRange.collapse(true)
                        sel.removeAllRanges()
                        sel.addRange(newRange)
                        editorRef.current?.focus()
                    }
                    setTimeout(updateFormatState, 0)
                    return
                }
            }
        }

        if (e.key === 'Escape') { setMentionQuery(null); setDropdownPos(null) }
        setTimeout(updateFormatState, 0)
    }

    const insertMention = (worker: { id: string; name: string }) => {
        if (!editorRef.current) return

        // Re-focus editor first (important on mobile where focus may have shifted)
        editorRef.current.focus()

        const sel = window.getSelection()

        // Use stored range if current selection is lost (common on mobile after touch)
        let range: Range | null = null
        if (sel && sel.rangeCount > 0) {
            range = sel.getRangeAt(0)
        } else if (lastMentionRangeRef.current) {
            range = lastMentionRangeRef.current
            sel?.removeAllRanges()
            sel?.addRange(range)
        }

        if (!range) return

        const node = range.startContainer
        if (node.nodeType !== Node.TEXT_NODE) return

        const textBeforeCaret = (node.textContent || '').slice(0, range.startOffset)
        const match = textBeforeCaret.match(/@([^\s]*)$/)
        if (!match) return

        const atIndex = textBeforeCaret.length - match[0].length

        const delRange = document.createRange()
        delRange.setStart(node, atIndex)
        delRange.setEnd(node, range.startOffset)
        delRange.deleteContents()

        const span = document.createElement('span')
        span.style.color = '#2563eb'
        span.style.fontWeight = '500'
        span.style.cursor = 'text'
        span.className = 'mention'
        span.setAttribute('data-worker-id', worker.id)
        span.contentEditable = 'false'
        span.textContent = `@${worker.name}`
        delRange.insertNode(span)

        const space = document.createTextNode('\u00A0')
        span.after(space)
        const newRange = document.createRange()
        newRange.setStartAfter(space)
        newRange.collapse(true)
        sel?.removeAllRanges()
        sel?.addRange(newRange)

        setMentionedWorkers(prev =>
            prev.find(w => w.id === worker.id) ? prev : [...prev, worker]
        )
        setMentionQuery(null)
        setDropdownPos(null)
        lastMentionRangeRef.current = null
        editorRef.current.focus()
    }

    const cleanNoteContent = (html: string): string => {
        const div = document.createElement('div')
        div.innerHTML = html
        div.querySelectorAll('li').forEach(li => {
            if (!li.textContent?.trim() && li.querySelectorAll('[data-worker-id]').length === 0) {
                li.remove()
            }
        })
        div.querySelectorAll('ul, ol').forEach(list => {
            if (list.children.length === 0) list.remove()
        })
        return div.innerHTML
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        const noteContent = cleanNoteContent(editorRef.current?.innerHTML || '')
        const plainText = editorRef.current?.innerText?.trim() || ''
        const newErrors: string[] = []

        // Check using innerText (plain text) — avoids false positives from HTML structure differences
        const templatePlainText = 'Raison du rendez-vous :Contexte et déroulement :Actions définies :'
        const isEffectivelyEmpty = !plainText || plainText.replace(/\s+/g, '') === templatePlainText.replace(/\s+/g, '')

        // The demo lets visitors go through the flow with empty fields.
        if (!IS_DEMO_MODE && isEffectivelyEmpty) {
            newErrors.push('Le contenu de la note est requis')
        }
        if (!IS_DEMO_MODE && !noteType) {
            newErrors.push('Le type de note est requis')
        }

        setErrors(newErrors)
        if (newErrors.length > 0) return

        onSubmit({
            ...noteData,
            type: noteType,
            note: noteContent,
            mentionedWorkerIds: mentionedWorkers.map(w => w.id),
            mentionedWorkerNames: mentionedWorkers.map(w => w.name)
        })
    }

    const executeCommand = (command: string, value?: string) => {
        editorRef.current?.focus()
        document.execCommand(command, false, value)
        setTimeout(updateFormatState, 0)
    }
    const insertBulletList = () => executeCommand('insertUnorderedList')
    const insertNumberedList = () => executeCommand('insertOrderedList')
    const insertLink = () => {
        const url = prompt('Entrez l\'URL du lien:')
        if (url) executeCommand('createLink', url)
    }
    const formatBold = () => executeCommand('bold')
    const formatItalic = () => executeCommand('italic')
    const formatUnderline = () => executeCommand('underline')

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader
                title="Nouvelle note"
                icon={<FileText size={20} />}
                onBack={onNavigateHome}
            />
            <div className="max-w-4xl mx-auto py-8 px-4">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
                    {errors.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                            <h3 className="text-red-800 font-medium">Informations manquantes :</h3>
                            <ul className="list-disc list-inside text-red-700">
                                {errors.map((error, index) => <li key={index}>{error}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom du.de la locataire</label>
                            <input type="text" value={noteData.locataire} disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Travailleur.euse</label>
                            <input type="text" value={noteData.travailleur} disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Collègue.s HF présent.e.s</label>
                            <input type="text"
                                value={Array.isArray(noteData.collegue) ? noteData.collegue.join(', ') : noteData.collegue}
                                disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date de la note</label>
                            <input type="date" value={noteData.date} disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type de note <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={noteType}
                                onChange={e => setNoteType(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${!noteType ? 'border-red-300' : 'border-gray-300'}`}
                            >
                                <option value="">— Sélectionner un type —</option>
                                {NOTE_TYPES.map(t => (
                                    <option key={t} value={t}>{capitalizeFirst(t)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note
                            <span className="ml-2 text-xs text-gray-400 font-normal">
                                Tapez @ pour mentionner un.e collègue · ↑↓ pour naviguer · Entrée pour sélectionner
                            </span>
                        </label>

                        {/* Toolbar */}
                        <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-t-md bg-gray-50">
                            <button type="button" onClick={formatBold}
                                className={`p-2 hover:bg-gray-200 rounded ${formatState.bold ? 'bg-blue-200 text-blue-800' : ''}`}
                                title="Gras"><Bold size={16} /></button>
                            <button type="button" onClick={formatItalic}
                                className={`p-2 hover:bg-gray-200 rounded ${formatState.italic ? 'bg-blue-200 text-blue-800' : ''}`}
                                title="Italique"><Italic size={16} /></button>
                            <button type="button" onClick={formatUnderline}
                                className={`p-2 hover:bg-gray-200 rounded ${formatState.underline ? 'bg-blue-200 text-blue-800' : ''}`}
                                title="Souligné"><Underline size={16} /></button>
                            <div className="w-px bg-gray-300 mx-1" />
                            <button type="button" onClick={insertBulletList}
                                className="p-2 hover:bg-gray-200 rounded" title="Liste à puces"><List size={16} /></button>
                            <button type="button" onClick={insertNumberedList}
                                className="p-2 hover:bg-gray-200 rounded" title="Liste numérotée"><ListOrdered size={16} /></button>
                            <div className="w-px bg-gray-300 mx-1" />
                            <button type="button" onClick={insertLink}
                                className="p-2 hover:bg-gray-200 rounded" title="Insérer un lien"><Link size={16} /></button>
                        </div>

                        {/* Editor */}
                        <div
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning
                            className="w-full min-h-[300px] px-3 py-2 border-l border-r border-b border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            style={{ lineHeight: '1.5', fontSize: '14px' }}
                            onInput={handleEditorInput}
                            onKeyDown={handleEditorKeyDown}
                            onFocus={updateFormatState}
                            onClick={handleEditorClick}
                        />

                        {/* @mention dropdown */}
                        {mentionQuery !== null && dropdownPos && (
                            <div
                                ref={dropdownRef}
                                style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 100 }}
                                className="bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto min-w-48"
                                onMouseDown={(e) => e.preventDefault()}
                                onTouchStart={(e) => e.preventDefault()}
                            >
                                {dropdownWorkers.length > 0 ? (
                                    <>
                                        {enabledFiltered.map((worker, idx) => (
                                            <button
                                                key={worker.id}
                                                type="button"
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                                    idx === activeDropdownIndex
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'hover:bg-blue-50 hover:text-blue-700'
                                                }`}
                                                onMouseDown={(e) => { e.preventDefault(); insertMention(worker) }}
                                                onTouchEnd={(e) => { e.preventDefault(); insertMention(worker) }}
                                            >
                                                <span className="text-blue-500">@</span>{worker.name}
                                            </button>
                                        ))}
                                        {disabledFiltered.length > 0 && enabledFiltered.length > 0 && (
                                            <div className="border-t border-gray-100 my-1" />
                                        )}
                                        {disabledFiltered.map(worker => (
                                            <div
                                                key={worker.id}
                                                className="w-full px-3 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center justify-between"
                                            >
                                                <span><span className="text-gray-300">@</span>{worker.name}</span>
                                                <span className="text-xs text-gray-300 ml-2">pas de profil</span>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-400">Aucun résultat</div>
                                )}
                            </div>
                        )}

                        {/* Mentions summary under editor */}
                        {mentionedWorkers.length > 0 && (
                            <div className="mt-2 text-xs text-gray-500">
                                Mentionné.e.s :{' '}
                                {mentionedWorkers.map(w => (
                                    <span key={w.id} className="text-blue-600 font-medium mr-1">@{w.name}</span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={onNavigateHome}>
                            Retour à l&apos;accueil
                        </Button>
                        <Button type="submit">Suivant</Button>
                    </div>
                </form>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                    title={confirmNavigateHomeCopy?.title}
                    message={confirmNavigateHomeCopy?.message}
                />
            )}
        </div>
    )
}
